import { ASK_FIRST_DETAILS } from "../job/details.js";
import { QUESTIONS } from "../job/worker.js";
import { arranged, missingRate } from "./card-arrangements.js";
/** Where the interview is: the field being asked, then confirm, then done. */
export const Slot = {
    trade: "trade",
    /** Which arrangements a maid or driver takes (ADR 0046). */
    arrangements: "arrangements",
    area: "area",
    rate: "rate",
    min: "min",
    /** The usual rate for each other arrangement picked. */
    more_rates: "more_rates",
    languages: "languages",
    about: "about",
    ask_first: "ask_first",
    auto: "auto",
    confirm: "confirm",
    done: "done",
};
/** The values tap choices carry besides trades, areas, languages and fields. */
export const Pick = { yes: "yes", no: "no", none: "none", skip: "skip", suggested: "suggested" };
/** The fields an owner can change from confirm, in the order the chips show them. */
export const FIELDS = [
    Slot.trade,
    Slot.area,
    Slot.rate,
    Slot.min,
    Slot.languages,
    Slot.about,
    Slot.auto,
];
const ORDER = [
    Slot.trade,
    Slot.arrangements,
    Slot.area,
    Slot.rate,
    Slot.min,
    Slot.more_rates,
    Slot.languages,
    Slot.about,
    Slot.ask_first,
    Slot.auto,
    Slot.confirm,
];
/** Yes-or-no details these trades' agents ask about, which the worker may want to see before any quote. */
export const askable = (trades) => [...ASK_FIRST_DETAILS].filter((k) => trades.some((tr) => QUESTIONS[tr].includes(k)));
/** The card names some work: a trade, or a skill of the worker's own. */
export const hasWork = (d) => !!d.trades?.length || !!d.skills?.length;
const complete = (d) => hasWork(d) &&
    d.area !== undefined &&
    d.rate_paise !== undefined &&
    d.limit_paise !== undefined &&
    d.limit_paise <= d.rate_paise &&
    missingRate(d) === undefined;
function firstMissing(d) {
    if (!hasWork(d))
        return Slot.trade;
    if (arranged(d) && d.arrangements === undefined)
        return Slot.arrangements;
    if (d.area === undefined)
        return Slot.area;
    if (d.rate_paise === undefined)
        return Slot.rate;
    if (d.limit_paise === undefined || d.limit_paise > d.rate_paise)
        return Slot.min;
    return complete(d) ? Slot.confirm : Slot.more_rates;
}
/** A new interview, at the first field, or pre-filled from the card the owner already has. */
export function start(config, existing = {}) {
    const draft = { ...existing };
    const ready = complete(draft);
    return {
        config,
        draft,
        slot: ready ? Slot.confirm : firstMissing(draft),
        notes: [],
        misses: 0,
        ask: 0,
        editing: false,
        // A whole card goes straight to what to change: that is why the owner is here.
        changing: ready,
        fresh: true,
    };
}
/** The same question again, after a note saying why. */
export const retry = (s, note) => ({ ...s, notes: [note], fresh: false });
function nextSlot(s, draft) {
    const asks = askable(draft.trades ?? []).length > 0;
    // A new trade may bring its own arrangements and ask-first question; new arrangements a rate in another unit; a new
    // rate may sit below the old minimum. Each other arrangement's rate is asked until none is missing.
    if (s.editing && s.slot === Slot.trade && arranged(draft))
        return Slot.arrangements;
    if (s.editing && s.slot === Slot.trade && asks)
        return Slot.ask_first;
    if (s.editing && s.slot === Slot.arrangements)
        return Slot.rate;
    if (s.editing && s.slot === Slot.rate && !complete(draft))
        return Slot.min;
    if (s.slot === Slot.more_rates && missingRate(draft))
        return Slot.more_rates;
    if (s.editing)
        return complete(draft) ? Slot.confirm : firstMissing(draft);
    const skipped = (slot) => (slot === Slot.ask_first && !asks) ||
        (slot === Slot.arrangements && !arranged(draft)) ||
        (slot === Slot.more_rates && !missingRate(draft));
    let i = ORDER.indexOf(s.slot) + 1;
    while (skipped(ORDER[i]))
        i++;
    return ORDER[i] ?? Slot.confirm;
}
/** The current field answered: on to the next one, with an optional readback. */
export function advance(s, draft, notes = []) {
    const slot = nextSlot(s, draft);
    const back = slot === Slot.confirm;
    return {
        ...s,
        draft,
        slot,
        notes,
        pending: undefined,
        pendingAloud: undefined,
        misses: 0,
        ask: 0,
        fresh: false,
        editing: !back && s.editing,
    };
}
/** Jumps to one field; after it is answered the interview returns to confirm. */
export function edit(state, slot) {
    const base = {
        ...state,
        notes: [],
        pending: undefined,
        pendingAloud: undefined,
        misses: 0,
        ask: 0,
        fresh: false,
        changing: false,
    };
    if (slot === Slot.confirm || slot === Slot.done)
        return { ...base, slot: Slot.confirm, editing: false };
    return { ...base, slot, editing: true };
}
export const draftOf = (state) => ({ ...state.draft });
/**
 * The same interview under new settings, for an owner who changed the app's language part-way: the questions from
 * here on, the words the recognizer expects and the language counted as their own follow the new one. The answers
 * stay; the readback already shown is not repeated when the question now open is asked again.
 */
export const withConfig = (state, config) => ({
    ...state,
    config,
    notes: [],
});
//# sourceMappingURL=card.js.map